import { useCallback, useRef, useState, useEffect, forwardRef, useImperativeHandle, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { 
  faRepeat, 
  faSyringe,
  faVolumeLow,
  faBoltLightning
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../Notifications';
import { useCPRSettings } from '../CPRSettings';
import { useCPRState } from '../CprState/CPRStateContext';
import { useCPRLog } from '../CPRLog';
import Metronome from '../Metronome';
import { useResusContext } from '../../Resus/ResusContext';
import { CprManagerActionSection } from './CprManagerActionSection';
import { TimerDisplay } from './CPRTimer';
import { cprEventEmitter, EVENTS } from '../cprEvents';
import './CprManager.scss';
import { saveCurrentState } from '../CprState';
import SettingsModal from '../SettingsModal';

export interface CprManagerRef {
  handleStartCpr: () => void;
}

// Define interfaces for our memoized components
interface CounterSectionProps {
  adrenalineCount: number;
  shockCount: number;
}

interface SettingsSectionProps {
  isSoundOn: boolean;
  handleSoundToggle: (value: boolean) => void;
  metronomeBitPerMinute: number;
  onSettingsClick: () => void;
}

// Memoized counter section to prevent re-renders
const CounterSection = memo(({ adrenalineCount, shockCount }: CounterSectionProps) => {
  const { t } = useTranslation(['resus']);
  
  return (
    <div className="counter-section">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <FontAwesomeIcon icon={faSyringe} />
        <span>{t('cpr.adrenalineCount', { count: adrenalineCount })}</span>
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <FontAwesomeIcon icon={faBoltLightning} />
        <span>{t('cpr.shockCount', { count: shockCount })}</span>
      </div>
    </div>
  );
});

// Memoized settings section to prevent re-renders
const SettingsSection = memo(({ isSoundOn, handleSoundToggle, metronomeBitPerMinute, onSettingsClick }: SettingsSectionProps) => {
  const { t } = useTranslation(['resus']);
  
  return (
    <div className="settings-section">
      <div className="sound-toggle">
        <label className="switch">
          <input
            type="checkbox"
            checked={isSoundOn}
            onChange={(e) => handleSoundToggle(e.target.checked)}
          />
          <span className="slider round"></span>
        </label>
        <FontAwesomeIcon icon={faVolumeLow} className="volume-icon" />
      </div>
      <div>
        <button className="btn btn-link"
                onClick={onSettingsClick}
                style={{direction: 'ltr', fontWeight: 'normal', fontSize: '0.8rem'}}>
                {t('cpr.bpm', { count: metronomeBitPerMinute || 100 })}
        </button>
      </div>
    </div>
  );
});

const CprManager = forwardRef<CprManagerRef>((_, ref) => {
  const { state, dispatch } = useCPRState();
  const { showNotification } = useNotification();
  const { settings } = useCPRSettings();
  const { addEntry } = useCPRLog();
  const { protocols, setProtocols } = useResusContext();
  const { t } = useTranslation(['resus']);
  
  const [isSoundOn, setIsSoundOn] = useState(() => {
    const savedState = localStorage.getItem('cprManagerSettings');
    return savedState ? JSON.parse(savedState).isSoundOn : true;
  });
  
  const [showDeathModal, setShowDeathModal] = useState(false);
  const [showROSCModal, setShowROSCModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const massagerNotificationShownRef = useRef(false);
  const adrenalineNotificationShownRef = useRef(false);
  const lastTickTimeRef = useRef<number>(Date.now());
  const checkNotificationsIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleSoundToggle = useCallback((value: boolean) => {
    setIsSoundOn(value);
    localStorage.setItem('cprManagerSettings', JSON.stringify({ isSoundOn: value }));
  }, []);

  const handleMassagerNotificationDismiss = useCallback(() => {
    dispatch({ type: 'SET_LAST_MASSAGER_TIME' });
    massagerNotificationShownRef.current = false;
  }, [dispatch]);

  // Handle adrenaline event subscription once
  useEffect(() => {
    const handleAdrenalineGiven = () => {
      adrenalineNotificationShownRef.current = false;
    };
  
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.ADRENALINE_GIVEN, handleAdrenalineGiven);
    return () => unsubscribe();
  }, []);

  // Handle reset CPR subscription once
  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      massagerNotificationShownRef.current = false;
      adrenalineNotificationShownRef.current = false;  
    });
    return () => unsubscribe();
  }, []);
  
  // Separate the notification checking to its own interval
  // This doesn't need to affect the UI render every second
  useEffect(() => {
    if (state.isRunning) {
      if (checkNotificationsIntervalRef.current) {
        clearInterval(checkNotificationsIntervalRef.current);
      }
      
      checkNotificationsIntervalRef.current = setInterval(() => {
        // Only dispatch tick to update internal time counter, but don't force renders
        dispatch({ type: 'TICK' });
        
        // Check for massager notification
        if (settings.massagerAlertEnabled && !massagerNotificationShownRef.current) {
          const timeSinceLastMassager = Date.now() - state.lastMassagerTime;
          const massagerIntervalMs = settings.massagerAlertSeconds * 1000;

          if (timeSinceLastMassager >= massagerIntervalMs) {
            massagerNotificationShownRef.current = true;
            showNotification({
              icon: faRepeat,
              text: t('cpr.exchangeMassagers'),                
              buttons: [{
                text: t('cpr.exchangeMassagersConfirmation'),
                className: "btn-update",
                onClick: handleMassagerNotificationDismiss
              }],
              onDismiss: handleMassagerNotificationDismiss
            });
          }
        }

        // Check for adrenaline notification
        if (settings.adrenalineAlertEnabled && !adrenalineNotificationShownRef.current) {
          const timeSinceLastAdrenaline = Date.now() - state.lastAdrenalineTime;
          const adrenalineIntervalMs = settings.adrenalineAlertSeconds * 1000;
          
          if (timeSinceLastAdrenaline >= adrenalineIntervalMs) {
            adrenalineNotificationShownRef.current = true;
            showNotification({
              icon: faSyringe,
              text: t('cpr.considerAdrenaline'),
              buttons: [
                {
                  text: t('cpr.goToMedications'),
                  className: "btn-update",
                  onClick: () => {                                          
                    cprEventEmitter.emit(EVENTS.ADD_MEDS_SECTION, "cardiac-arrest");
                  }
                }                
              ]                
            });
          }
        }
      }, 1000);

      lastTickTimeRef.current = Date.now();
    } else if (checkNotificationsIntervalRef.current) {
      clearInterval(checkNotificationsIntervalRef.current);
      checkNotificationsIntervalRef.current = null;
    }

    return () => {
      if (checkNotificationsIntervalRef.current) {
        clearInterval(checkNotificationsIntervalRef.current);
      }
    };
  }, [state.isRunning, state.lastMassagerTime, state.lastAdrenalineTime, settings, dispatch, showNotification, handleMassagerNotificationDismiss, t]);

  const handleStartCpr = useCallback(() => {
    if (!protocols.includes('cardiac-arrest')) {
      const newProtocols = [...protocols, 'cardiac-arrest'];
      setProtocols(newProtocols);
      saveCurrentState({
        resusContext: {
          protocols: newProtocols
        }
      }, 'resusContext');
      addEntry({
        timestamp: new Date().toISOString(),
        text: t('cpr.cardiacArrestProtocolAdded'),
        type: 'openingConditions',
        isImportant: false
      });
    }
    cprEventEmitter.emit(EVENTS.START_CPR);
    dispatch({ type: 'START_CPR' });
    massagerNotificationShownRef.current = false;
    adrenalineNotificationShownRef.current = false;
    addEntry({
      timestamp: new Date().toISOString(),
      text: t('cpr.cprStarted'),
      type: 'action',
      isImportant: false
    });
  }, [protocols, dispatch, setProtocols, addEntry, t]);

  const handleDeathButtonClick = useCallback(() => {
    setShowDeathModal(true);
  }, []);

  const handleConfirmDeath = useCallback(() => {
    dispatch({ type: 'END_CPR', status: 'DEATH', time: Date.now() });
    massagerNotificationShownRef.current = false;
    adrenalineNotificationShownRef.current = false;
    setShowDeathModal(false);
    addEntry({
      timestamp: new Date().toISOString(),
      text: t('cpr.patientDeath'),
      type: 'action',
      isImportant: false
    });
  }, [dispatch, addEntry, t]);

  const handleROSCButtonClick = useCallback(() => {
    setShowROSCModal(true);
  }, []);

  const handleConfirmROSC = useCallback(() => {
    dispatch({ type: 'END_CPR', status: 'ROSC', time: Date.now() });
    massagerNotificationShownRef.current = false;
    adrenalineNotificationShownRef.current = false;
    setShowROSCModal(false);
    addEntry({
      timestamp: new Date().toISOString(),
      text: t('cpr.cprSuccess'),
      type: 'action',
      isImportant: false
    });
  }, [dispatch, addEntry, t]);
  
  const handleOpenSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  const handleCloseDeathModal = useCallback(() => {
    setShowDeathModal(false);
  }, []);

  const handleCloseROSCModal = useCallback(() => {
    setShowROSCModal(false);
  }, []);

  useImperativeHandle(ref, () => ({
    handleStartCpr
  }), [handleStartCpr]);

  return (
    <div className="cpr-manager-container">
    <div className="cpr-manager-mgmt-panel">
      <div className="timer-section">
        {/* Counters on the right - Memoized */}
        <CounterSection 
          adrenalineCount={state.adrenalineCount} 
          shockCount={state.shockCount} 
        />
        
        {/* Timers in the middle - Separated component that handles its own rendering */}
        <TimerDisplay state={state} settings={settings} />

        {/* Settings section on the left - Memoized */}
        <SettingsSection 
          isSoundOn={isSoundOn} 
          handleSoundToggle={handleSoundToggle}
          metronomeBitPerMinute={settings.metronomeBitPerMinute}
          onSettingsClick={handleOpenSettings}
        />
      </div>
      
      <SettingsModal
        runningCprMode={state.isRunning}
        isOpen={showSettingsModal}
        onClose={handleCloseSettings}
      />
      
      <Metronome isPlaying={state.isRunning && isSoundOn} bpm={settings.metronomeBitPerMinute} />
    </div>

    <CprManagerActionSection
      showDeathMessage={state.status === 'DEATH'}
      showSuccessMessage={state.status === 'ROSC'}
      deathTime={state.status === 'DEATH' ? state.endTime : null}
      successTime={state.status === 'ROSC' ? state.endTime : null}
      showDeathModal={showDeathModal}
      showROSCModal={showROSCModal}
      onROSCClick={handleROSCButtonClick}
      onDeathClick={handleDeathButtonClick}
      onConfirmROSC={handleConfirmROSC}
      onConfirmDeath={handleConfirmDeath}
      onCloseDeathModal={handleCloseDeathModal}
      onCloseROSCModal={handleCloseROSCModal}
    />
    </div>
  );
});

CprManager.displayName = 'CprManager';

export default CprManager;